import { create } from "zustand";
import { persist } from "zustand/middleware";

import { env } from "@demo/env";

export const DEV_MODE_STORAGE_KEY = "perceptron:demo:dev-mode";

/** Environments that ship developer mode without anyone asking for it. */
const AUTO_DEV_MODE_ENVS = new Set(["development", "staging"]);

interface DevModeStore {
  /**
   * Granted by the query param, and the only half of availability worth
   * storing — deriving the rest means an environment that stops being staging
   * stops offering developer mode, rather than leaving it switched on in
   * whatever browsers happened to visit it first.
   */
  isGranted: boolean;
  /** The user's own choice. `null` is "whatever availability implies". */
  preference: boolean | null;
  grant: () => void;
  setActive: (active: boolean) => void;
}

/**
 * Developer mode, in two parts.
 *
 * `available` is whether this browser may use developer mode at all: true on
 * development and staging, and on any environment that has been handed the
 * query param.
 */
export const useDevModeStore = create<DevModeStore>()(
  persist(
    (set) => ({
      isGranted: false,
      preference: null,
      grant: () => set({ isGranted: true }),
      setActive: (active) => set({ preference: active }),
    }),
    { name: DEV_MODE_STORAGE_KEY },
  ),
);

export const selectIsDevModeAvailable = (state: DevModeStore): boolean =>
  state.isGranted || AUTO_DEV_MODE_ENVS.has(env.NEXT_PUBLIC_APP_ENV);

export const selectIsDevModeActive = (state: DevModeStore): boolean =>
  selectIsDevModeAvailable(state) && (state.preference ?? true);

/** May this browser use developer mode? Gates the toggle itself. */
export const useIsDevModeAvailable = (): boolean => useDevModeStore(selectIsDevModeAvailable);

/** Is developer mode switched on? Gates the features behind it. */
export const useIsDevModeActive = (): boolean => useDevModeStore(selectIsDevModeActive);
